import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { NgbPopoverModule } from "@ng-bootstrap/ng-bootstrap";
import { VngCheckboxComponent, VngIconComponent, VngTooltipDirective } from "@vengreso/angular-components";

/**
 * Collapsed-row content for a FlyMSG (FlyCut/FlyPlate) accordion row.
 *
 * Projected into a host's `vng-expansion-panel` via `[vngExpansionHeader]` —
 * the chevron/expand affordance stays OWNED by `vng-expansion-panel` itself
 * (rendered as a sibling of the projected header content, see
 * `vng-expansion-panel.component.html`), so this component never renders its
 * own chevron.
 *
 * The row's bulk-select checkbox is, on every current host (dashboard,
 * shortcuts), rendered as a SIBLING of `vng-expansion-panel` rather than
 * inside the header — kept there deliberately (checkbox bulk-select logic
 * lives at the host level). `showCheckbox`/`checked`/`checkedChange` are
 * provided here only for a host that prefers to project it inside the header
 * row instead; they default to off and are unused by the dashboard widget.
 */
@Component({
  selector: "app-flymsg-row-header",
  standalone: true,
  imports: [CommonModule, FormsModule, NgbPopoverModule, VngCheckboxComponent, VngIconComponent, VngTooltipDirective],
  templateUrl: "./flymsg-row-header.component.html",
  styleUrls: ["./flymsg-row-header.component.scss"],
})
export class FlymsgRowHeaderComponent {
  /** FlyCut shortcode/name, rendered in the dashed chip (e.g. "-trpk"). */
  @Input() name = "";

  /** Optional title shown next to the name (shortcuts/FlyPlates rows). Dashboard leaves this empty. */
  @Input() title = "";

  /**
   * Pre-highlighted (search-match) HTML for the title — takes priority over
   * `title` when set. T3 additive Input: shortcuts highlights `shortcut.title`
   * only `*ngIf="fromSearch"`; FlyPlates rows highlight `template.title`
   * unconditionally (searchText is "" outside search mode, which the
   * HighlighterPipe passes through unchanged). Mirrors `descriptionHtml`.
   */
  @Input() titleHtml: string | null = null;

  /** First-line/description preview shown on the row's second line. */
  @Input() description = "";

  /** Pre-highlighted (search-match) HTML for the description. Takes priority over `description` when set. */
  @Input() descriptionHtml: string | null = null;

  @Input() descriptionPlaceholder = "First line of content appears here...";

  /** Shows the small "PREMIUM" badge next to the name (FlyPlates library rows). */
  @Input() premium = false;

  /** The pill badge with the clock icon + count. */
  @Input() showUsageBadge = true;
  @Input() usageCount = 0;
  @Input() usageLabel = "Versions";
  @Output() usageClick = new EventEmitter<void>();

  /** The small bar-chart stats icon + hover popover (times used / time saved). Dashboard-only. */
  @Input() showStats = false;
  @Input() timesUsed: number | null = null;
  @Input() timeSavedHours: number | null = null;
  @Output() statsClick = new EventEmitter<void>();

  /**
   * Per-row error message rendered as a warning line under the row (settings'
   * Import FlyCuts preview: "FlyCut exceeds the character limit of your plan").
   * Empty (default) renders nothing — no other host has a row-level error.
   */
  @Input() errorText = "";

  /** Optional checkbox projected inside the header row — see class doc. Off by default. */
  @Input() showCheckbox = false;
  @Input() checked = false;
  @Output() checkedChange = new EventEmitter<boolean>();

  get timeSavedDisplay(): string {
    const value = this.timeSavedHours ?? 0;
    return typeof value === "number" ? value.toFixed(2) : `${value}`;
  }

  onCheckedChange(value: boolean): void {
    this.checked = value;
    this.checkedChange.emit(value);
  }
}
